import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { articleAiError, articleGenerationApi } from '@shared/api';
import { Button } from '@shared/ui';

type Basis = { articleId?: string; content: string; title: string; revision: number };
type Intent = Basis & { key: string };

/** Suggestions never persist; a late response cannot replace a newer editor revision. */
export function ArticleTitleAction({
  articleId,
  content,
  title,
  revision,
  disabled,
  onTitle,
}: Basis & { disabled: boolean; onTitle: (title: string) => void }) {
  const { t } = useTranslation();
  const current = useRef({ articleId, content, title, revision, disabled, onTitle });
  current.current = { articleId, content, title, revision, disabled, onTitle };
  const mounted = useRef(true);
  const intent = useRef<Intent | undefined>(undefined);
  const busy = useRef(false);
  const [pending, setPending] = useState(false);
  const [recovering, setRecovering] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const generate = async () => {
    if (busy.current || disabled) return;
    const captured = intent.current ?? {
      articleId,
      content,
      title,
      revision,
      key: crypto.randomUUID(),
    };
    intent.current = captured;
    busy.current = true;
    setPending(true);
    try {
      const result = await articleGenerationApi.title(
        {
          content: captured.content,
          ...(captured.articleId ? { articleId: captured.articleId } : {}),
        },
        captured.key
      );
      const bytes = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(captured.content)
      );
      const hash = Array.from(new Uint8Array(bytes), (byte) =>
        byte.toString(16).padStart(2, '0')
      ).join('');
      const output = result.data.output;
      if (
        output.basis.articleId !== (captured.articleId ?? null) ||
        output.basis.contentSha256 !== hash
      )
        throw new Error('Article title basis mismatch');
      intent.current = undefined;
      if (!mounted.current) return;
      setRecovering(false);
      const latest = current.current;
      if (
        !latest.disabled &&
        latest.articleId === captured.articleId &&
        latest.revision === captured.revision &&
        latest.content === captured.content &&
        latest.title === captured.title &&
        output.currentAtCompletion !== false
      ) {
        latest.onTitle(output.title);
        toast.success(t('articles.ai.suggested'));
      } else toast.warning(t('articles.ai.stale'));
    } catch (error) {
      if (!mounted.current) return;
      const code = articleAiError(error);
      const unknown = code === 'unknown';
      if (!unknown) intent.current = undefined;
      setRecovering(unknown);
      if (unknown || code === 'stale') toast.warning(t(`articles.ai.${code}`));
      else toast.error(t(`articles.ai.${code}`));
    } finally {
      busy.current = false;
      if (mounted.current) setPending(false);
    }
  };
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      loading={pending}
      disabled={
        disabled ||
        pending ||
        (!recovering && (!content.trim() || content.length > 100000 || content.includes('\0')))
      }
      onClick={() => {
        void generate();
      }}
    >
      {!pending ? <Sparkles aria-hidden="true" /> : null}
      {t(
        pending
          ? 'articles.ai.generatingTitle'
          : recovering
            ? 'articles.ai.checkResult'
            : 'articles.ai.generateTitle'
      )}
    </Button>
  );
}
